import React from "react";
import * as XLSX from "xlsx";
import { fromJS, List } from "immutable";
import { action } from "store/Project";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import { Modal, Input, Upload, Button, Form, message } from "antd";
import { InboxOutlined, DownloadOutlined } from "@ant-design/icons";

import { API } from "utils/api";
import { Get, Post } from "utils/request";
import { checkSuggestSum } from "utils/score";

const { Dragger } = Upload;
const { Item: FormItem } = Form;

/**
 * 评分表模板的列位置（0 基），与业务方约定的模板结构一一对应：
 *   A 基本因素层 | B 建议分值 | C 因素层 | D 建议分值 | E 因子层 | F 建议分值
 * 第 1 行是表头，数据从第 2 行开始。
 */
const COL = {
  basicName: 0,
  basicScore: 1,
  normalName: 2,
  normalScore: 3,
  subName: 4,
  subScore: 5
};

const EXCEL_PATTERN = /\.(xlsx|xls)$/i;

/**
 * 评分表模板放在 public/ 下，CRA 会原样拷到构建产物根目录。
 * 用 PUBLIC_URL 拼接，将来若配置了 homepage 部署到子路径也不会失效。
 */
const TEMPLATE_URL = `${process.env.PUBLIC_URL}/评分表模板.xlsx`;

/** 去首尾空白（含中文全角空格），用于比对模板名称与因子库名称 */
const normalizeName = (value) =>
  value == null ? "" : String(value).replace(/^[\s\u3000]+|[\s\u3000]+$/g, "");

/** Excel 单元格转分值：空值返回 null（区分「没填」与「填了 0」） */
const toScore = (value) => {
  if (value == null || value === "") return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

const readFileAsArrayBuffer = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsArrayBuffer(file);
  });

/**
 * 把工作表中的合并单元格「向下/向右填充」。
 *
 * 模板里基本因素层、因素层、因子层的名称都是合并单元格（如「宏观区位影响度」
 * 纵向合并 3 行），sheet_to_json 默认只在左上角格子有值、其余为 null，
 * 不先展开的话层级关系会整片丢失。
 */
const expandMergedCells = (worksheet) => {
  const merges = worksheet["!merges"] || [];
  merges.forEach((range) => {
    const topLeftAddress = XLSX.utils.encode_cell({ r: range.s.r, c: range.s.c });
    const topLeft = worksheet[topLeftAddress];
    if (!topLeft) return;
    for (let r = range.s.r; r <= range.e.r; r++) {
      for (let c = range.s.c; c <= range.e.c; c++) {
        const address = XLSX.utils.encode_cell({ r, c });
        if (!worksheet[address]) {
          worksheet[address] = { ...topLeft };
        }
      }
    }
  });
  return worksheet;
};

/**
 * 解析模板文件，返回按层级归并后的三层结构（只有名称与分值，尚未查 id）。
 *
 * 归并口径：
 * - basic 按 name 去重
 * - normal 按 (所属 basic + name) 去重
 * - sub 按 (所属 basic + 所属 normal + name) 去重
 * 不同父级下完全可能存在同名因子，只按 name 去重会把它们错误合并。
 */
const parseTemplate = async (file) => {
  const buffer = await readFileAsArrayBuffer(file);
  const workbook = XLSX.read(new Uint8Array(buffer), { type: "array" });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    throw new Error("文件中没有任何工作表");
  }

  const worksheet = expandMergedCells(workbook.Sheets[sheetName]);
  const rows = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    defval: null,
    blankrows: false
  });

  const basics = [];
  const normals = [];
  const subs = [];

  rows.slice(1).forEach((row) => {
    const basicName = normalizeName(row[COL.basicName]);
    if (!basicName) return;

    const normalName = normalizeName(row[COL.normalName]);
    const subName = normalizeName(row[COL.subName]);

    if (!basics.some((b) => b.name === basicName)) {
      basics.push({
        name: basicName,
        suggest_score: toScore(row[COL.basicScore])
      });
    }

    if (
      normalName &&
      !normals.some((n) => n.basicName === basicName && n.name === normalName)
    ) {
      normals.push({
        name: normalName,
        basicName,
        suggest_score: toScore(row[COL.normalScore])
      });
    }

    if (
      subName &&
      !subs.some(
        (s) =>
          s.basicName === basicName &&
          s.normalName === normalName &&
          s.name === subName
      )
    ) {
      subs.push({
        name: subName,
        basicName,
        normalName,
        suggest_score: toScore(row[COL.subScore])
      });
    }
  });

  return { basics, normals, subs };
};

/**
 * 用 /factor/list 的结果把「名称」翻译成「因子 id」。
 *
 * 覆盖 suggest_score 和 status：
 * - suggest_score 沿用模板解析结果（空值为 null）
 * - 模板中 suggest_score 为空 → 自动视为"不用填（100分）"→ status = 0
 * - 模板中 suggest_score 有值 → status 沿用因子库默认值（通常为 1）
 * - 其余字段（type / minimum / maximum 等）全部沿用因子库的值，
 *   与手动创建评分表（ADD_BASIC 等 reducer）保持一致。
 *
 * @returns {{basics:Array, normals:Array, subs:Array, errors:string[]}}
 */
const mapToFactors = (parsed, factorList) => {
  const errors = [];
  const basicLib = factorList.get("basic") || new List();
  const normalLib = factorList.get("normal") || new List();
  const subLib = factorList.get("sub") || new List();

  /** 模板中未填 suggest_score → 勾选不用填（status=0） */
  const resolveStatus = (target, suggestScore) =>
    suggestScore == null ? 0 : target.get("status", 1);

  const basics = [];
  const basicIdByName = {};

  parsed.basics.forEach((item) => {
    const target = basicLib.find(
      (f) => normalizeName(f.get("name")) === item.name
    );
    if (!target) {
      errors.push(`基本因素「${item.name}」在因子库中不存在`);
      return;
    }
    basicIdByName[item.name] = target.get("id");
    basics.push({
      ...target.toJS(),
      suggest_score: item.suggest_score,
      status: resolveStatus(target, item.suggest_score)
    });
  });

  const normals = [];
  const normalIdByKey = {};

  parsed.normals.forEach((item) => {
    const parentId = basicIdByName[item.basicName];
    const target = normalLib.find(
      (f) =>
        normalizeName(f.get("name")) === item.name &&
        (parentId == null || f.get("parent_id") === parentId)
    );
    if (!target) {
      errors.push(`因素「${item.name}」在因子库中不存在`);
      return;
    }
    normalIdByKey[`${item.basicName}/${item.name}`] = target.get("id");
    normals.push({
      ...target.toJS(),
      suggest_score: item.suggest_score,
      status: resolveStatus(target, item.suggest_score)
    });
  });

  const subs = [];

  parsed.subs.forEach((item) => {
    const parentId = normalIdByKey[`${item.basicName}/${item.normalName}`];
    const target = subLib.find(
      (f) =>
        normalizeName(f.get("name")) === item.name &&
        (parentId == null || f.get("parent_id") === parentId)
    );
    if (!target) {
      errors.push(`因子「${item.name}」在因子库中不存在`);
      return;
    }
    subs.push({
      ...target.toJS(),
      suggest_score: item.suggest_score,
      status: resolveStatus(target, item.suggest_score)
    });
  });

  return { basics, normals, subs, errors };
};

class UploadTableModal extends React.Component {
  state = { submitting: false };

  formRef = React.createRef();

  handleClose = () => {
    if (this.state.submitting) return;
    this.formRef.current.resetFields();
    this.props.closeUploadTableModal();
  };

  handleSubmit = async () => {
    let values;
    try {
      values = await this.formRef.current.validateFields();
    } catch (e) {
      return;
    }

    const { title, file } = values;
    this.setState({ submitting: true });

    try {
      // 解析模板与拉取因子库并行：两者互不依赖
      const [parsed, factorListRes] = await Promise.all([
        parseTemplate(file[0].originFileObj),
        Get(API.FACTOR_LIST)
      ]);

      if (!parsed.basics.length) {
        message.error("未能从文件中解析出评分表数据，请检查是否使用了正确的模板");
        return;
      }

      const { basics, normals, subs, errors } = mapToFactors(
        parsed,
        fromJS(factorListRes)
      );

      if (errors.length) {
        const preview = errors.slice(0, 3).join("、");
        message.error(
          errors.length > 3
            ? `模板解析失败：${preview} 等 ${errors.length} 项与因子库不匹配`
            : `模板解析失败：${preview}`
        );
        return;
      }

      let data = fromJS({
        title: title.trim(),
        project_id: this.props.match.params.id,
        basic: basics,
        normal: normals,
        sub: subs
      });

      // 与创建评分表弹窗保持同一套校验口径：basic 全部 = 100，normal/sub 每组 = 100
      const { basicSum, basicOk, normalOk, subOk } = checkSuggestSum(data);

      if (!basicOk) {
        message.error(`基本因素层建议分值合计为 ${basicSum}，必须等于 100`);
        return;
      }
      if (!normalOk) {
        message.error("因素层各分组的建议分值合计必须等于 100");
        return;
      }
      if (!subOk) {
        message.error("因子层各分组的建议分值合计必须等于 100");
        return;
      }

      // 与 CreateTableModal 提交前处理一致：按 id / parent_id 排序
      data = data
        .set("basic", data.get("basic").sortBy((i) => i.get("id")))
        .set("normal", data.get("normal").sortBy((i) => i.get("parent_id")))
        .set("sub", data.get("sub").sortBy((i) => i.get("parent_id")));

      const res = await Post(API.TABLE_BASE, data);

      const newProject = this.props.project.set(
        "tables",
        this.props.project.get("tables").push(fromJS(res))
      );

      this.props.changeProject(newProject);
      message.success(
        `上传成功（基本因素 ${basics.length} / 因素 ${normals.length} / 因子 ${subs.length}）`
      );
      this.formRef.current.resetFields();
      this.props.closeUploadTableModal();
    } catch (e) {
      message.error(
        (e && e.message) || "文件解析失败，请确认上传的是评分表模板文件"
      );
    } finally {
      this.setState({ submitting: false });
    }
  };

  render() {
    const { uploadTableModalVisible } = this.props;
    const { submitting } = this.state;

    return (
      <Modal
        title="上传评分表"
        visible={uploadTableModalVisible}
        onCancel={this.handleClose}
        maskClosable={false}
        footer={[
          <Button key="cancel" disabled={submitting} onClick={this.handleClose}>
            取消
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={submitting}
            onClick={this.handleSubmit}
          >
            上传
          </Button>
        ]}
      >
        <Form ref={this.formRef} layout="vertical">
          <FormItem
            label="评分表名称"
            name="title"
            rules={[{ required: true, message: "请填写评分表名称" }]}
          >
            <Input placeholder="请输入评分表名称" />
          </FormItem>

          <FormItem
            label="上传文件"
            name="file"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              const fileList = Array.isArray(e) ? e : e.fileList;
              // maxCount=1，只保留最后一个文件（重新选文件时自动替换上一个）
              return fileList && fileList.length ? [fileList[fileList.length - 1]] : [];
            }}
            rules={[
              { required: true, message: "请上传文件" },
              {
                validator: (rule, value) =>
                  value && value.length && EXCEL_PATTERN.test(value[0].name)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error("只支持 .xlsx / .xls 格式的评分表模板")
                      )
              }
            ]}
          >
            <Dragger maxCount={1} beforeUpload={() => false}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此处上传</p>
              <p className="ant-upload-hint">
                可下载模板进行填写
              </p>
            </Dragger>
          </FormItem>

          <div style={{ marginTop: -12, textAlign: "right" }}>
            <a href={TEMPLATE_URL} download>
              <DownloadOutlined /> 下载评分表模板
            </a>
          </div>
        </Form>
      </Modal>
    );
  }
}

const STP = (state) => {
  return {
    uploadTableModalVisible: state.getIn(["project", "uploadTableModalVisible"]),
    project: state.getIn(["project", "project"])
  };
};

const DTP = (dispatch) => {
  return {
    closeUploadTableModal: () => {
      dispatch(action.changeItem(["uploadTableModalVisible"], false));
    },
    changeProject: (newProject) => {
      dispatch(action.changeItem(["project"], newProject));
    }
  };
};

export default withRouter(connect(STP, DTP)(UploadTableModal));
